// See App.tsx's own comment on this same import -- required for the
// classic JSX transform this app's tsconfig uses.
import * as React from 'react';
import { useEffect, useRef } from 'react';
import type { GuidedConversationView, GuidedReview, GuidedSectionId } from 'employment-insurance-data-access';
import type { GuidedTextResolver } from './guided-text';

export interface ReviewPageProps {
  view: GuidedConversationView;
  review: GuidedReview;
  t: GuidedTextResolver;
  busy: boolean;
  submitting: boolean;
  submitError: boolean;
  onChange: (section: GuidedSectionId | 'intro') => void;
  onAttest: (confirmed: boolean) => void;
  onBack: () => void;
  onSubmit: () => void;
}

const STATEMENTS = ['attest.s1', 'attest.s2', 'attest.s3', 'attest.s4', 'attest.s5'] as const;

/**
 * The review page (content map §10): a full page, not the prototype's
 * modal. The "Since your last report" row and the six sections, each with
 * a Change link that re-enters the conversation at that section; then the
 * five attestation statements with one confirming checkbox. Submit stays
 * disabled until it is ticked -- and the BFF refuses it anyway.
 *
 * Focus moves to the page heading when it opens, a plain focusable <h1>
 * (scds-section-heading's heading sits in its shadow root, where it can't
 * take focus).
 */
export function ReviewPage({ view, review, t, busy, submitting, submitError, onChange, onAttest, onBack, onSubmit }: ReviewPageProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  const period = { t: 'range' as const, from: view.period.firstDay, to: view.period.lastDay };

  function changeLink(section: GuidedSectionId | 'intro', name: string) {
    return (
      <button
        type="button"
        className="ei-guided__change"
        data-testid={`guided-change-${section}`}
        aria-label={t({ key: 'review.changeLabel', params: { section: { t: 'text', v: name } } })}
        disabled={busy}
        onClick={() => onChange(section)}
      >
        {t({ key: 'review.change' })}
      </button>
    );
  }

  const introName = t({ key: 'review.introHeading' });

  return (
    <section className="ei-guided-review" aria-labelledby="ei-guided-review-title" data-testid="guided-review">
      <div className="ei-guided-review__intro">
        <h1 id="ei-guided-review-title" ref={headingRef} tabIndex={-1} className="ei-guided__title">
          {t({ key: 'review.title' })}
        </h1>
        <p className="ei-guided__subtitle">{t({ key: 'review.period', params: { period } })}</p>
        {review.prefillSource ? (
          <p className="ei-guided__note">
            {t({ key: review.prefillSource === 'lastReport' ? 'review.prefilledLastReport' : 'review.prefilledApplication' })}
          </p>
        ) : null}
      </div>

      <div className="ei-guided-review__card">
        <div className="ei-guided-review__card-head">
          <h2 className="ei-guided-review__card-title">{introName}</h2>
          {changeLink('intro', introName)}
        </div>
        <dl className="ei-guided-review__rows">
          <div className="ei-guided-review__row">
            <dt>{t(review.intro.question)}</dt>
            <dd>{t(review.intro.answer)}</dd>
          </div>
        </dl>
      </div>

      {review.sections.map((section) => {
        const name = t({ key: `section.${section.section}` });
        return (
          <div className="ei-guided-review__card" key={section.section} data-section={section.section}>
            <div className="ei-guided-review__card-head">
              <span className="ei-guided-review__number" aria-hidden="true">
                {section.number}
              </span>
              <h2 className="ei-guided-review__card-title">{name}</h2>
              {changeLink(section.section, name)}
            </div>
            <dl className="ei-guided-review__rows">
              {section.rows.map((row, i) => (
                <div className="ei-guided-review__row" key={i}>
                  <dt>{t(row.question)}</dt>
                  <dd>{t(row.answer)}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}

      <div className="ei-guided-review__attest">
        <h2 className="ei-guided-review__attest-heading">{t({ key: 'review.attest.heading' })}</h2>
        <ul className="ei-guided-review__statements">
          {STATEMENTS.map((key) => (
            <li key={key}>{t({ key: `review.${key}`, params: key === 'attest.s1' ? { period } : undefined })}</li>
          ))}
        </ul>
        <label className="ei-guided-review__confirm">
          <input
            type="checkbox"
            checked={review.attested}
            disabled={busy}
            data-testid="guided-attest"
            onChange={(event) => onAttest(event.target.checked)}
          />
          <span>{t({ key: 'review.attest.confirm' })}</span>
        </label>
      </div>

      {submitError ? (
        <p role="alert" className="ei-guided__note ei-guided__note--error">
          {t({ key: 'review.submitError' })}
        </p>
      ) : null}

      <div className="ei-guided-review__actions">
        {view.canGoBack ? (
          <scds-button variant="secondary" type="button" data-testid="guided-back-to-edit" disabled={busy || submitting} onClick={() => !(busy || submitting) && onBack()}>
            {t({ key: 'review.backToEdit' })}
          </scds-button>
        ) : null}
        <scds-button
          variant="primary"
          type="button"
          data-testid="guided-submit-report"
          disabled={!view.canSubmit || busy || submitting}
          onClick={() => {
            if (view.canSubmit && !busy && !submitting) onSubmit();
          }}
        >
          {t({ key: 'review.submit' })}
        </scds-button>
      </div>
    </section>
  );
}
